import { Button, Card, Col, Input, Result, Row, Typography, Select, Space, Table, message, DatePicker, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ErrorLog } from '../../models/error-log-interface';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { ErrorLogQueries } from '../../queries/error-log-queries';
import { GetDateFormateStr, TimeFromDate, TimeWithTimeZone } from '../../utilites/dateTimeHelper';

const { Title } = Typography;
const { Option } = Select;

function LogsPage() {
  const [appFilter, setAppFilter] = useState<string[]>([]);
  const [machineFilter, setMachineFilter] = useState<string | null>(null);
  const [messageFilter, setMessageFilter] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Moment>(moment().startOf('day'));
  const [machineList, setMachineList] = useState<string[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ErrorLog[]>([]);

  const apiErrorLogQuery = CustomerServiceQueries.useErrorLogsSinceDateList(selectedDate);
  const appErrorLogQuery = ErrorLogQueries.useErrorLogList(selectedDate);
  /*const salesforceLogQuery = CustomerServiceQueries.useSalesforceLogList();*/

  useEffect(() => {
    if(!!apiErrorLogQuery?.data) {
      var machineList = apiErrorLogQuery.data.map(d => d.machine);
      var distinctMachineList = [...new Set(machineList)];

      setMachineList(distinctMachineList);
    }
  }, [apiErrorLogQuery?.data])

  /** Filter change use effect  **/
  useEffect(() => {
    if(!!apiErrorLogQuery?.data || !!appErrorLogQuery?.data) {
      var allLogs = [...(apiErrorLogQuery?.data ?? []), ...(appErrorLogQuery?.data ?? [])];

      if(appFilter.length > 0) {
        allLogs = allLogs.filter(l => appFilter.some(f => parseInt(f, 10) == l.applicationId) || appFilter.some(f => f == l.application?.toLowerCase()));
      }

      if(machineFilter && (appFilter.length == 0 || appFilter.some(a => a == "api" || a == "salesforce"))) {
        allLogs = allLogs.filter(l => l.machine === machineFilter);
      }

      if(messageFilter != null) {
        allLogs = allLogs.filter(l => l.exception?.toLowerCase()?.includes((messageFilter as string).toLowerCase()) ||
            l.description?.toLowerCase()?.includes((messageFilter as string).toLowerCase()) ||
            l.message?.toLowerCase()?.includes((messageFilter as string).toLowerCase()) ||
            l.stacktrace?.toLowerCase()?.includes((messageFilter as string).toLowerCase()));
      }

      setFilteredLogs(allLogs);
    }
  }, [apiErrorLogQuery.data, appErrorLogQuery.data, appFilter, machineFilter, messageFilter])

  

  const getAppName = (appId: number | string) => {
    switch(appId) {
      case 1:
        return 'Web App'
      case 2:
        return 'Admin'
      case 3: 
        return 'iOS'
      case 4:
        return 'Android'
      case 5:
        return 'CS Portal'
      case "Salesforce":
        return "Salesforce"
      default:
        return 'Api'
    }
  }

  let columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => {
        return `${moment(date).format(GetDateFormateStr(1))} ${moment.utc(date).toDate().toLocaleTimeString()}`;
      },
      sorter: {
        compare: (a, b) => {
            let d1 = a.date ?? new Date("01/01/1000");
            let d2 = b.date ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
        }
      },
      defaultSortOrder: "descend",
      fixed: 'left',
    },
    {
      title: 'App',
      dataIndex: 'applicationId',
      key: 'applicationId',
      render: (applicationId: number, record: ErrorLog) => {
        return getAppName(applicationId ?? record.application)
      },
      fixed: 'left',
    },
  ]

  if(appFilter.length == 0 || appFilter.some(a => a == "api" || a == "salesforce")) {
    columns.push({
      title: 'Server',
      dataIndex: 'machine',
      key: 'machine',
      render: (machine: string) => {
        return machine
      },
    })
  }

  if(appFilter.length == 0 || !appFilter.some(a => a == "api" || a == "salesforce")) {
    columns.push({
      title: 'User Id',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: number) => {
        return userId
      },
    })
  }

  columns.push({
    title: 'Message',
    dataIndex: 'exception',
    key: 'exception',
    render: (exception: string, record: ErrorLog) => {
      return <div style={{whiteSpace: "pre-line", wordBreak: 'break-all'}}>
        <b>{record.description ?? record.message}</b>
        <p>{exception ?? record.stacktrace}</p>
      </div>
    },
    textWrap: 'word-break',
  })

  return (
    <Card>
      <Title level={3}>Error Logs</Title>
      <Row gutter={[16, 16]}>
        <Col flex="auto">
          <Input
            placeholder='Filter'
            onChange={(e) => setMessageFilter(e.target.value ?? null)}
            value={messageFilter}
          />
        </Col>
        <Col>
          <Space>
            <Select
              placeholder={"Filter by App"}
              value={appFilter}
              onChange={(val) => setAppFilter(val)}
              onClear={() => setAppFilter(null)}
              allowClear
              mode='tags'
              style={{minWidth: 200}}
            >
              <Option
                value="2"
                key="admin"
              >
                Admin
              </Option>
              <Option
                value="1"
                key="web"
              >
                Web App
              </Option>
              <Option
                value="5"
                key="csportal"
              >
                CS Portal
              </Option>
              <Option
                value="api"
                key="api"
              >
                Api
              </Option>
              <Option
                value="salesforce"
                key="salesforce"
              >
                Salesforce
              </Option>
            </Select>
            {(appFilter.length == 0 || appFilter.some(a => a == "api" || a == "salesforce")) && (
              <Select
                placeholder={"Filter by Server"}
                value={machineFilter}
                onSelect={(val) => setMachineFilter(val)}
                onClear={() => setMachineFilter(null)}
                allowClear
              >
                {machineList.map(machine => {
                  return (<Option key={machine} value={machine}>{machine}</Option>)
                })}
              </Select>
            )}
            <Tooltip title="Select the date of the logs you want to see">
              <DatePicker 
                value={selectedDate}
                onChange={(date) => setSelectedDate((date as Moment).startOf('day'))}
                allowClear={false}
                disabledDate={(date) => date.isAfter(moment().startOf('day'))}
              />
            </Tooltip>
          </Space>
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            size="middle"
            dataSource={filteredLogs ?? []}
            rowKey={(val, index) => `${index}-${val.date}`}
            scroll
          />
        </Col>
      </Row>
    </Card>
  );
}

export default LogsPage;
